// features/products/services/productsApi.ts (COMPLETE - FINAL)
/**
 * Products API Service
 * 
 * Complete API client for the Product module.
 * All endpoints match the backend routes exactly.
 */

import apiClient from '@/lib/api-client';

// ============================================================
// TYPES
// ============================================================

export interface ProductData {
    id: string;
    tenantId?: string;
    name: string;
    slug: string;
    productType: string;
    description: string | null;
    shortDescription?: string | null;
    brand: string | null;
    manufacturer?: string | null;
    categoryId: string;
    categoryName?: string;
    supplierId?: string | null;
    supplierName?: string | null;
    tags?: string[];
    barcode?: string | null;
    sku?: string | null;
    mpn?: string | null;
    upc?: string | null;
    ean?: string | null;
    imageUrls: string[];
    thumbnailUrl?: string | null;
    retailPrice: string;
    wholesalePrice: string | null;
    clearancePrice: string | null;
    costPrice: string;
    minimumPrice?: string | null;
    maximumDiscount?: string | null;
    taxRate: string;
    taxInclusive: boolean;
    taxCategory?: string | null;
    isActive: boolean;
    isClearance: boolean;
    isFeatured?: boolean;
    hasVariants: boolean;
    trackInventory: boolean;
    stockManagement?: string;
    lowStockThreshold: number;
    reorderPoint?: number | null;
    reorderQuantity?: number | null;
    deletedAt?: string | null;
    isPharmaceutical?: boolean;
    requiresPrescription?: boolean;
    drugClassification?: string | null;
    activeIngredients?: string | null;
    storageRequirements?: string | null;
    hasExpiry?: boolean;
    shelfLifeDays?: number | null;
    isAgeRestricted?: boolean;
    minimumAge?: number | null;
    requiresID?: boolean;
    weight?: string | null;
    weightUnit?: string | null;
    length?: string | null;
    width?: string | null;
    height?: string | null;
    dimensionUnit?: string | null;
    isReturnable?: boolean;
    returnPeriodDays?: number | null;
    variants: ProductVariantData[];
    uoms: ProductUomData[];
    barcodes?: ProductBarcodeData[];
    batches?: ProductBatchData[];
    storeSpecificPrices?: StoreSpecificPriceData[];
    stockLevels?: ProductStockLevel[];
    customFields?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    seoTitle?: string | null;
    seoDescription?: string | null;
    createdAt: string;
    updatedAt: string;
    createdBy?: string | null;
    updatedBy?: string | null;
}

export interface ProductVariantData {
    id: string;
    productId: string;
    name: string;
    sku: string;
    barcode: string | null;
    retailPrice: string | null;
    wholesalePrice: string | null;
    clearancePrice: string | null;
    costPrice: string | null;
    stockQuantity: string;
    isActive: boolean;
    metadata: string | null;
    attributes?: Record<string, string>;
    sortOrder?: number;
    createdAt: string;
    updatedAt: string;
}

export interface ProductUomData {
    id: string;
    productId: string;
    name: string;
    abbreviation: string;
    conversionRate: string;
    isBaseUom: boolean;
    barcode: string | null;
    retailPrice: string | null;
    wholesalePrice: string | null;
    clearancePrice: string | null;
    isActive: boolean;
    sortOrder?: number;
    createdAt: string;
    updatedAt: string;
}

export interface ProductBarcodeData {
    id: string;
    productId: string;
    barcode: string;
    barcodeType: string;
    isPrimary: boolean;
    uomId: string | null;
    createdAt: string;
}

export interface ProductBatchData {
    id: string;
    tenantId: string;
    productId: string;
    batchNumber: string;
    manufacturingDate: string | null;
    expiryDate: string | null;
    quantity: string;
    costPrice: string | null;
    supplierId: string | null;
    notes: string | null;
    isActive?: boolean;
    createdAt: string;
    updatedAt?: string;
}

export interface ProductStockLevel {
    branchId: string;
    branchName?: string;
    quantityOnHand: string;
    availableQuantity: string;
    reservedQuantity: string;
    confirmationStatus: 'pending' | 'counted' | 'confirmed';
    lastCountedAt: string | null;
}

export interface ProductImageData {
    id: string;
    productId: string;
    url: string;
    thumbnailUrl: string | null;
    alt: string | null;
    isPrimary: boolean;
    sortOrder: number;
    fileName: string;
    fileSize: number;
    mimeType: string;
    width: number | null;
    height: number | null;
    createdAt: string;
}

export interface StoreSpecificPriceData {
    storeId: string;
    retailPrice: string;
    wholesalePrice: string | null;
    clearancePrice: string | null;
}

export interface ProductsListResponse {
    data: ProductData[];
    meta: {
        cursor: string | null;
        hasNextPage: boolean;
        totalCount: number;
    };
}

export interface ProductStatsData {
    total: number;
    active: number;
    inactive: number;
    clearance: number;
    pharmaceutical: number;
}

export interface BulkUploadResult {
    uploadId: string;
    totalRows: number;
    successCount: number;
    errorCount: number;
    skippedCount?: number;
    errors: BulkUploadError[];
    hasErrors: boolean;
    categoriesCreated?: number;
    vendorsCreated?: number;
}

export interface BulkUploadError {
    row: number;
    productName?: string;
    field: string;
    message: string;
    suggestedFix?: string;
}


export interface BulkUploadPreview {
    totalRows: number;
    validRows: number;
    warningRows: number;
    errorRows: number;
    importableRows: number; // ✅ Add this
    preview: PreviewRow[];
    errors: BulkUploadError[];
    newCategories: string[];
    columnMapping: Record<string, string>;
}

export interface PreviewRow {
    row: number;
    name: string;
    categorySlug: string;
    retailPrice: string;
    costPrice: string;
    sku: string;
    barcode: string;
    baseUom: string;
    status: 'valid' | 'warning' | 'error';
    warnings: string[];
    errors: string[];
}


// ============================================================
// PRODUCT CRUD
// ============================================================

export async function getProducts(
    tenantId: string,
    params: Record<string, unknown> = {}
): Promise<ProductsListResponse> {
    const finalParams = {
        excludeRecipes: true,
        ...params,
    };
    const response = await apiClient.get(`/tenants/${tenantId}/products`, { params: finalParams });
    const responseData = response.data;

    if (responseData?.data && Array.isArray(responseData.data)) {
        return {
            data: responseData.data,
            meta: {
                cursor: responseData.meta?.cursor || null,
                hasNextPage: responseData.meta?.hasNextPage || false,
                totalCount: responseData.meta?.totalCount || responseData.data.length,
            },
        };
    }

    if (Array.isArray(responseData)) {
        return { data: responseData, meta: { cursor: null, hasNextPage: false, totalCount: responseData.length } };
    }

    return { data: [], meta: { cursor: null, hasNextPage: false, totalCount: 0 } };
}

export async function getProduct(tenantId: string, productId: string): Promise<ProductData> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/products/${productId}`);
    return data.data as ProductData;
}

export async function createProduct(tenantId: string, payload: Record<string, unknown>): Promise<ProductData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products`, payload);
    return data.data;
}

export async function updateProduct(tenantId: string, productId: string, payload: Record<string, unknown>): Promise<ProductData> {
    const { data } = await apiClient.patch(`/tenants/${tenantId}/products/${productId}`, payload);
    return data.data;
}

export async function deleteProduct(tenantId: string, productId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/products/${productId}`);
}

export async function restoreProduct(tenantId: string, productId: string): Promise<ProductData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/${productId}/restore`);
    return data.data;
}

export async function duplicateProduct(tenantId: string, productId: string, options?: { name?: string; slug?: string }): Promise<ProductData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/${productId}/duplicate`, options || {});
    return data.data;
}

export async function toggleClearance(tenantId: string, productId: string): Promise<ProductData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/${productId}/toggle-clearance`);
    return data.data;
}

export async function getProductStats(tenantId: string): Promise<ProductStatsData> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/products/stats`);
    return data.data;
}

export async function bulkDeleteProducts(tenantId: string, productIds: string[]): Promise<{ deletedCount: number; requestedCount: number }> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/bulk-delete`, { productIds });
    return data.data;
}

// ============================================================
// CATEGORIES
// ============================================================

export async function getCategories(tenantId: string): Promise<Array<{ id: string; name: string; slug: string; parentId: string | null; color?: string }>> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/categories`);
    return data.data || [];
}

// ============================================================
// TAGS
// ============================================================

export async function getProductTags(tenantId: string, search?: string): Promise<string[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/products/tags`, { params: { search } });
    return data.data || [];
}

// ============================================================
// STOCK LEVELS
// ============================================================

export async function getProductStockLevels(tenantId: string, productId: string): Promise<ProductStockLevel[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/products/${productId}/stock-levels`);
    return data.data || [];
}

// ============================================================
// PRODUCT IMAGES
// ============================================================

export async function getProductImages(tenantId: string, productId: string): Promise<ProductImageData[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/products/${productId}/images`);
    return data.data || [];
}

export async function uploadProductImage(tenantId: string, productId: string, file: File, options?: { alt?: string; isPrimary?: boolean }): Promise<ProductImageData> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.alt) formData.append('alt', options.alt);
    if (options?.isPrimary) formData.append('isPrimary', 'true');
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/${productId}/images/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.data;
}

export async function addProductImage(tenantId: string, productId: string, payload: { url: string; alt?: string; isPrimary?: boolean; sortOrder?: number }): Promise<ProductImageData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/${productId}/images`, payload);
    return data.data;
}

export async function updateProductImage(tenantId: string, productId: string, imageId: string, payload: { alt?: string; isPrimary?: boolean }): Promise<void> {
    await apiClient.patch(`/tenants/${tenantId}/products/${productId}/images/${imageId}`, payload);
}

export async function deleteProductImage(tenantId: string, productId: string, imageId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/products/${productId}/images/${imageId}`);
}

export async function reorderProductImages(tenantId: string, productId: string, imageIds: string[]): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/products/${productId}/images/reorder`, { imageIds });
}

export async function setPrimaryProductImage(tenantId: string, productId: string, imageId: string): Promise<void> {
    await apiClient.post(`/tenants/${tenantId}/products/${productId}/images/${imageId}/primary`);
}

// ============================================================
// VARIANTS
// ============================================================

export async function addProductVariant(tenantId: string, productId: string, payload: {
    name: string; sku: string; barcode?: string;
    retailPrice?: string; wholesalePrice?: string; clearancePrice?: string;
    costPrice?: string; stockQuantity?: string;
}): Promise<ProductVariantData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/${productId}/variants`, payload);
    return data.data;
}

export async function updateProductVariant(tenantId: string, variantId: string, payload: Record<string, unknown>): Promise<void> {
    await apiClient.patch(`/tenants/${tenantId}/products/variants/${variantId}`, payload);
}

export async function deleteProductVariant(tenantId: string, variantId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/products/variants/${variantId}`);
}

// ============================================================
// UOMS
// ============================================================

export async function addProductUom(tenantId: string, productId: string, payload: {
    name: string; abbreviation: string; conversionRate: string;
    isBaseUom: boolean; barcode?: string;
    retailPrice?: string; wholesalePrice?: string; clearancePrice?: string;
}): Promise<ProductUomData> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/${productId}/uoms`, payload);
    return data.data;
}

export async function updateProductUom(tenantId: string, uomId: string, payload: Record<string, unknown>): Promise<void> {
    await apiClient.patch(`/tenants/${tenantId}/products/uoms/${uomId}`, payload);
}

export async function deleteProductUom(tenantId: string, uomId: string): Promise<void> {
    await apiClient.delete(`/tenants/${tenantId}/products/uoms/${uomId}`);
}

// ============================================================
// BULK UPLOAD
// ============================================================

export async function downloadTemplate(tenantId: string): Promise<Blob> {
    const response = await apiClient.get(`/tenants/${tenantId}/products/template/download`, { responseType: 'blob' });
    return response.data;
}

export async function bulkUploadProducts(
    tenantId: string,
    file: File,
    options?: { skipDuplicates?: boolean }
): Promise<BulkUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.skipDuplicates) {
        formData.append('skipDuplicates', 'true');
    }

    const { data } = await apiClient.post(
        `/tenants/${tenantId}/products/bulk-upload`,
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
            params: options,
        }
    );
    return data?.data || data;
}

export async function downloadBulkUploadErrorReport(tenantId: string, uploadId: string): Promise<Blob> {
    const response = await apiClient.get(`/tenants/${tenantId}/products/bulk-upload/${uploadId}/errors`, { responseType: 'blob' });
    return response.data;
}

export async function previewBulkUpload(
    tenantId: string,
    file: File
): Promise<BulkUploadPreview> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post(
        `/tenants/${tenantId}/products/bulk-upload/preview`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data?.data || data;
}

// ============================================================
// DUPLICATE CHECK, MERGE & PRICE HISTORY
// ============================================================

export interface DuplicateCheckResult {
    isExactMatch: boolean;
    exactMatchField?: 'barcode' | 'sku' | 'name';
    exactProduct?: { id: string; name: string; sku: string; barcode: string; retailPrice: string; costPrice: string };
    similarProducts: Array<{ id: string; name: string; sku: string; barcode: string; similarity: number; stockOnHand?: string }>;
}

export async function checkDuplicateProduct(
    tenantId: string,
    params: { name?: string; sku?: string; barcode?: string; excludeId?: string }
): Promise<DuplicateCheckResult> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/products/check-duplicate`, { params });
    return data?.data || data;
}

export async function mergeProducts(
    tenantId: string,
    payload: { targetProductId: string; sourceProductId: string; reason?: string }
): Promise<{ targetProduct: ProductData; mergedStockTransferred: number }> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/products/merge`, payload);
    return data?.data || data;
}

export interface PriceHistoryEntry {
    id: string;
    priceType: string;
    oldPrice: string | null;
    newPrice: string;
    reason: string;
    changedByName: string;
    createdAt: string;
}

export async function getProductPriceHistory(tenantId: string, productId: string): Promise<PriceHistoryEntry[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/products/${productId}/price-history`);
    return data?.data || data;
}